// src/app/app-routing.module.ts
import { NgModule } from '@angular/core';
import { PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { AuthGuard } from '@core/guards/auth.guard';
import { businessModeGuard } from '@core/guards/business-mode.guard';
import { alreadyAuthGuard } from '@core/guards/already-auth.guard';
import { requiresDowngradePickerGuard } from '@core/guards/requires-downgrade-picker.guard';

const routes: Routes = [
  {
    path: 'login',
    canActivate: [alreadyAuthGuard],
    loadChildren: () => import('./features/auth/auth.module').then(m => m.AuthModule)
  },
  {
    path: 'onboarding/business-type',
    canActivate: [AuthGuard],
    loadComponent: () => import('./features/onboarding/business-type/business-type.component')
      .then(m => m.BusinessTypeComponent)
  },
  {
    path: '',
    canActivate: [AuthGuard, businessModeGuard],
    children: [
      {
        path: 'dashboard',
        loadChildren: () => import('./features/dashboard/dashboard.routes')
          .then(m => m.DASHBOARD_ROUTES)
      },
      {
        path: 'projects',
        loadChildren: () => import('./features/projects/projects.routes')
          .then(m => m.PROJECT_ROUTES)
      },
      {
        path: 'tasks',
        loadChildren: () => import('./features/tasks/tasks.routes')
          .then(m => m.TASKS_ROUTES)
      },
      {
        path: 'clients',
        loadChildren: () => import('./features/clients/clients.routes')
          .then(m => m.CLIENTS_ROUTES)
      },
      {
        path: 'finance',
        loadChildren: () => import('./features/finance/finance.routes')
          .then(m => m.FINANCE_ROUTES)
      },
      {
        path: 'contracts',
        loadChildren: () => import('./features/contracts/contracts.routes')
          .then(m => m.CONTRACTS_ROUTES)
      },
      {
        path: 'profile',
        loadChildren: () => import('./features/profile/profile.routes')
          .then(m => m.PROFILE_ROUTES)
      },
      {
        path: 'settings',
        loadChildren: () => import('./features/settings/settings.routes')
          .then(m => m.SETTINGS_ROUTES)
      },
      {
        path: 'business-settings',
        loadComponent: () => import('./features/business-settings/components/business-settings/business-settings.component')
          .then(m => m.BusinessSettingsComponent)
      },
      {
        path: 'admin',
        loadChildren: () => import('./features/admin/admin.module')
          .then(m => m.AdminModule)
      },
      {
        path: 'reviews/submit',
        loadComponent: () => import('./features/reviews/components/submit-review/submit-review.component')
          .then(m => m.SubmitReviewComponent)
      },
      {
        path: 'email',
        loadChildren: () => import('./features/email/email.routes')
          .then(m => m.EMAIL_ROUTES)
      },
      {
        path: 'setup-profile',
        loadComponent: () => import('./features/setup-profile/components/setup-profile/setup-profile.component')
          .then(m => m.SetupProfileComponent)
      },
      {
        path: 'billing/select-active-clients',
        canActivate: [requiresDowngradePickerGuard],
        loadComponent: () => import('./features/billing/components/downgrade-picker/downgrade-picker.component')
          .then(m => m.DowngradePickerComponent)
      },
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full'
      }
    ]
  },
  // EZ Type's connect page (public): www.ezatwork.com/eztype/connect redirects
  // here with the desktop app's sign-in request. Outside EZ@Work's shell.
  {
    path: 'eztype/connect',
    loadComponent: () => import('./features/eztype-connect/eztype-connect.component')
      .then(m => m.EzTypeConnectComponent)
  },
  {
    path: 'auth/demo',
    loadComponent: () => import('./features/auth/components/demo/demo.component')
      .then(m => m.DemoComponent)
  },
  {
    path: 'refresh-token',
    loadComponent: () => import('./features/auth/components/refresh-token/refresh-token.component')
      .then(m => m.RefreshTokenComponent)
  },
  // Static pages (public access)
  {
    path: 'about',
    loadComponent: () => import('./features/static-pages/components/about/about.component')
      .then(m => m.AboutComponent)
  },
  {
    path: 'help',
    loadComponent: () => import('./features/static-pages/components/help/help.component')
      .then(m => m.HelpComponent)
  },
  {
    path: 'faq',
    loadComponent: () => import('./features/static-pages/components/faq/faq.component')
      .then(m => m.FaqComponent)
  },
  {
    path: 'documentation',
    loadComponent: () => import('./features/static-pages/components/documentation/documentation.component')
      .then(m => m.DocumentationComponent)
  },
  {
    path: 'privacy',
    loadComponent: () => import('./features/static-pages/components/privacy/privacy.component')
      .then(m => m.PrivacyComponent)
  },
  {
    path: 'terms',
    loadComponent: () => import('./features/static-pages/components/terms/terms.component')
      .then(m => m.TermsComponent)
  },
  {
    path: 'pricing',
    loadComponent: () => import('./features/static-pages/components/pricing/pricing.component')
      .then(m => m.PricingComponent)
  },
  {
    path: 'pricing/success',
    loadComponent: () => import('./features/static-pages/components/pricing-success/pricing-success.component')
      .then(m => m.PricingSuccessComponent)
  },
  {
    path: 'cookies',
    loadComponent: () => import('./features/static-pages/components/cookies/cookies.component')
      .then(m => m.CookiesComponent)
  },
  {
    path: 'security',
    loadComponent: () => import('./features/static-pages/components/security/security.component')
      .then(m => m.SecurityComponent)
  },
  // Client Portal (public, token-based, no auth required)
  {
    path: 'portal',
    loadChildren: () => import('./features/client-portal/client-portal.routes')
      .then(m => m.CLIENT_PORTAL_ROUTES)
  },
  // Contract e-signature (public, token-based, no auth required)
  {
    path: 'sign',
    loadChildren: () => import('./features/sign/sign.routes')
      .then(m => m.SIGN_ROUTES)
  },
  // Design preview (redesign foundation — Phase 1)
  {
    path: 'design-preview',
    loadChildren: () => import('./features/design-preview/design-preview.routes')
      .then(m => m.DESIGN_PREVIEW_ROUTES)
  },
  // Loader preview (for development/testing)
  {
    path: 'loader-preview',
    loadComponent: () => import('./features/loader-preview/loader-preview.component')
      .then(m => m.LoaderPreviewComponent)
  },
  {
    path: 'loader-preview-v2',
    loadComponent: () => import('./features/loader-preview/loader-preview-v2.component')
      .then(m => m.LoaderPreviewV2Component)
  },
  {
    path: 'loaders-comparison',
    loadComponent: () => import('./features/loader-preview/loaders-comparison.component')
      .then(m => m.LoadersComparisonComponent)
  },
  {
    path: 'accessibility',
    loadComponent: () => import('./features/static-pages/components/accessibility/accessibility.component')
      .then(m => m.AccessibilityComponent)
  },
  {
    path: '**',
    redirectTo: 'login'
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { preloadingStrategy: PreloadAllModules })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
